import { documentKeywordsService } from '@/services/document-keywords.service';
import axios from 'axios';

const initialState = () => ({
  keywords: [],
  categories: [],
  status: {
    error: null,
    pending: true,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

const actions = {
  getKeywords({ commit }) {
    commit('GET_KEYWORDS');
    return documentKeywordsService.getKeywords().then(
      (keywords) => {
        commit('SUCCESS', keywords);
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('ERROR', error);
          throw error;
        }
      },
    );
  },

  addDocumentKeywordsBulk({ commit }, { categoryId, keywords }) {
    commit('ADD_DOCUMENT_KEYWORD');
    return documentKeywordsService.addKeywordsBulk(categoryId, keywords).then(
      (created) => {
        commit('ADD_KEYWORDS_SUCCESS');
        return created;
      },
      (error) => {
        commit('ERROR', error);
        throw error;
      },
    );
  },

  // A write that fails, cancelled or not, is never resolved: the caller would report it as done
  updateDocumentKeyword({ commit }, { categoryId, keywordId, updatedKeyword }) {
    commit('UPDATE_DOCUMENT_KEYWORD');
    return documentKeywordsService.updateKeyword(categoryId, keywordId, updatedKeyword).then(
      (updatedKeywordData) => {
        commit('UPDATE_KEYWORD_SUCCESS', updatedKeywordData);
      },
      (error) => {
        commit('ERROR', error);
        throw error;
      },
    );
  },
  deleteDocumentKeywords({ commit }, { categoryId, keywordIds }) {
    commit('DELETE_DOCUMENT_KEYWORDS');
    return documentKeywordsService.deleteDocumentKeywordsBulk(categoryId, keywordIds).then(
      () => {
        commit('DELETE_KEYWORDS_SUCCESS', { categoryId, keywordIds });
      },
      (error) => {
        commit('ERROR', error);
        throw error;
      },
    );
  },
  addCategoryDoc({ commit }, { language, name, colorCode }) {
    commit('ADD_CATEGORY');
    return documentKeywordsService.addCategory(language, name, colorCode).then(
      (newCategory) => {
        commit('ADD_CATEGORY_SUCCESS', newCategory);
      },
      (error) => {
        commit('ERROR', error);
        throw error;
      },
    );
  },
  deleteDocumentCategory({ commit }, { id }) {
    commit('DELETE_DOCUMENT_CATEGORY');
    return documentKeywordsService.deleteCategory(id).then(
      () => {
        commit('DELETE_CATEGORY_SUCCESS', id);
      },
      (error) => {
        commit('ERROR', error);
        throw error;
      },
    );
  },
  updateDocumentCategory({ commit }, { categoryId, name, colorCode }) {
    commit('UPDATE_DOCUMENT_CATEGORY');
    return documentKeywordsService.updateCategory(categoryId, { name, colorCode }).then(
      (updatedCategory) => {
        commit('UPDATE_CATEGORY_SUCCESS', updatedCategory);
      },
      (error) => {
        commit('ERROR', error);
        throw error;
      },
    );
  },
};

const mutations = {
  // The list stays shown until the new one replaces it.
  GET_KEYWORDS(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  SUCCESS(state, keywords) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.keywords = keywords;
    state.categories = keywords.flatMap((cat) => cat);
  },
  ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  ADD_DOCUMENT_KEYWORD(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  // The categories are reloaded after an add, which brings the new keywords
  ADD_KEYWORDS_SUCCESS(state) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
  },
  UPDATE_DOCUMENT_KEYWORD(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  UPDATE_KEYWORD_SUCCESS(state, updatedKeyword) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    const index = state.keywords.findIndex((keyword) => keyword.id === updatedKeyword.id);
    if (index !== -1) {
      state.keywords.splice(index, 1, updatedKeyword);
    }
  },
  DELETE_DOCUMENT_KEYWORDS(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },

  DELETE_KEYWORDS_SUCCESS(state, { categoryId, keywordIds }) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    const category = state.categories.find((cat) => cat.id === categoryId);

    if (category) {
      if (Array.isArray(category.keywords)) {
        category.keywords = category.keywords.filter((keyword) => !keywordIds.includes(keyword.id));
      } else {
        category.keywords = [];
      }
    }
  },

  ADD_CATEGORY(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  ADD_CATEGORY_SUCCESS(state, newCategory) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.categories.push(newCategory);
  },
  DELETE_DOCUMENT_CATEGORY(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  DELETE_CATEGORY_SUCCESS(state, categoryId) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.categories = state.categories.filter((category) => category.id !== categoryId);
  },
  UPDATE_DOCUMENT_CATEGORY(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  UPDATE_CATEGORY_SUCCESS(state, updatedCategory) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    const index = state.categories.findIndex((category) => category.id === updatedCategory.id);
    if (index !== -1) {
      state.categories.splice(index, 1, updatedCategory);
    }
  },
  RESET(state) {
    const initial = initialState();
    Object.keys(initial).forEach((key) => {
      state[key] = initial[key];
    });
  },
};

const getters = {
  categories: (state) => state.categories,
  keywords: (state) => state.keywords,
  flattenKeywords: (state) => flattenKeywords(state.keywords),

  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
};

export const documentKeywords = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};

const flattenKeywords = (keywords = []) =>
  keywords.reduce((keywords, category) => keywords.concat(category.keywords), []);
